import { LazyPathwayDiagram } from '@/components/diagrams/LazyPathwayDiagram';
import { EditableText } from '@/components/editor/EditableText';
import { FICHE_COLOR_HEX } from '@/lib/colors';
import { renderMarkdownLite } from '@/lib/markdown';
import type { Fiche, FicheColor, FicheSection } from '@pharmafiche/schema';
import { Fragment } from 'react';
import type { FicheEditHandlers } from './FicheDessinee';

interface FicheFlowchartProps {
  fiche: Fiche;
  editHandlers?: FicheEditHandlers;
}

/**
 * Phase 6 — pathway-first template.
 *
 * Very light slate background. Space Grotesk title, JetBrains Mono accents.
 * The diagram dominates 60-70% of the page; a side panel summarises it. Below,
 * the drug cards/side-effects render in a 3-column grid.
 */
export function FicheFlowchart({ fiche, editHandlers }: FicheFlowchartProps) {
  const diagramSection = fiche.sections.find((s) => s.type === 'diagram');
  const diagramId = diagramSection?.type === 'diagram' ? diagramSection.diagram_id : null;
  const diagram = diagramId ? fiche.diagrams.find((d) => d.id === diagramId) : null;
  const drugCards = fiche.sections.filter((s) => s.type === 'drug_card');
  const otherSections = fiche.sections.filter(
    (s) => s.type !== 'diagram' && s.type !== 'drug_card' && s.type !== 'comparison_table',
  );

  return (
    <div
      className="min-h-screen text-ink"
      style={{
        background: '#f7f8fa',
        WebkitFontSmoothing: 'antialiased',
        fontFamily: 'Space Grotesk, sans-serif',
      }}
    >
      <div className="max-w-[1100px] mx-auto px-6 md:px-10 pt-8 pb-12">
        <header className="mb-6">
          <div
            className="uppercase tracking-[0.22em] text-[11px] text-ink-soft mb-1"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            Mécanisme d'action
          </div>
          <h1
            className="font-space font-bold leading-tight"
            style={{ fontSize: 28, letterSpacing: '-0.01em' }}
          >
            <EditableText
              value={fiche.meta.title}
              plain
              ariaLabel="Titre"
              onCommit={(t) => editHandlers?.updateMetaTitle?.(t)}
            />
          </h1>
          {fiche.meta.subtitle ? (
            <p className="text-[14px] text-ink-soft mt-1">{fiche.meta.subtitle}</p>
          ) : null}
        </header>

        {/* Diagram + legend */}
        <div className="grid grid-cols-1 lg:grid-cols-[2fr,1fr] gap-4 mb-8">
          <div className="bg-white border border-ink/15 rounded-control p-3 overflow-x-auto">
            {diagram ? (
              <LazyPathwayDiagram diagram={diagram} />
            ) : (
              <div className="p-8 text-center text-ink-soft text-[14px]">
                {diagramSection
                  ? `Diagramme ${diagramSection.type === 'diagram' ? diagramSection.diagram_id : ''} introuvable dans la fiche.`
                  : "Aucun diagramme dans cette fiche. Demande à l'IA : « ajoute un diagramme du mécanisme »."}
              </div>
            )}
          </div>
          <aside className="bg-white border border-ink/15 rounded-control p-3 flex flex-col gap-3">
            <h2 className="font-space font-bold text-[14px] uppercase tracking-wider">Légende</h2>
            <ul className="m-0 pl-0 list-none text-[13px] flex flex-col gap-1">
              <LegendItem color="green" label="Récepteur / cible" />
              <LegendItem color="blue" label="Enzyme / voie biochimique" />
              <LegendItem color="purple" label="Sous-unité / domaine" />
              <LegendItem color="red" label="Effet à inhiber" />
              <LegendItem color="yellow" label="Plaquettes / agrégat" />
            </ul>
            <div className="border-t border-ink/15 pt-2">
              <h3 className="font-space font-bold text-[12px] uppercase tracking-wider mb-1">
                Résumé
              </h3>
              <p className="text-[13px] leading-[1.55] text-ink-soft">{summaryFor(fiche)}</p>
            </div>
          </aside>
        </div>

        {/* Drug cards in a 3-column grid */}
        {drugCards.length > 0 ? (
          <>
            <h2 className="font-space font-bold text-[16px] uppercase tracking-wider mb-2">
              Molécules
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {drugCards.map((sec) =>
                sec.type === 'drug_card' ? (
                  <CompactDrugCard key={sec.id} section={sec} editHandlers={editHandlers} />
                ) : null,
              )}
            </div>
          </>
        ) : null}

        {/* Side-effects / mnemo / warning / case */}
        {otherSections.length > 0 ? (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {otherSections.map((sec) => (
              <OtherSection key={sec.id} section={sec} editHandlers={editHandlers} />
            ))}
          </div>
        ) : null}
      </div>
    </div>
  );
}

function LegendItem({ color, label }: { color: FicheColor; label: string }) {
  return (
    <li className="flex items-center gap-2">
      <span
        aria-hidden
        style={{
          width: 12,
          height: 12,
          borderRadius: 3,
          background: FICHE_COLOR_HEX[color],
          border: '1.5px solid #1f2330',
        }}
      />
      <span>{label}</span>
    </li>
  );
}

function summaryFor(fiche: Fiche): string {
  // Pull the first intro bullet as a one-line summary, fall back to subtitle.
  for (const sec of fiche.sections) {
    if (sec.type === 'intro' && sec.bullets[0]) {
      return sec.bullets[0].text.replace(/\*\*/g, '');
    }
  }
  return fiche.meta.subtitle ?? `Fiche ${fiche.meta.title}.`;
}

function CompactDrugCard({
  section,
  editHandlers,
}: {
  section: Extract<FicheSection, { type: 'drug_card' }>;
  editHandlers?: FicheEditHandlers;
}) {
  const { drug } = section;
  const accent = FICHE_COLOR_HEX[drug.color];
  return (
    <article
      data-section-id={section.id}
      className="bg-white rounded-control p-3"
      style={{ borderLeft: `4px solid ${accent}` }}
    >
      <h3 className="font-space font-bold text-[15px] mb-1" style={{ color: accent }}>
        <EditableText
          value={drug.name}
          plain
          ariaLabel="Molécule"
          onCommit={(t) => editHandlers?.updateDrugName?.(section.id, t)}
        />
      </h3>
      <ul className="m-0 pl-4 list-disc text-[13px] leading-[1.5]">
        {drug.intro_bullets.map((b) => (
          <li key={b.id} data-bullet-id={b.id}>
            <EditableText
              value={b.text}
              boldColor={accent}
              ariaLabel="Bullet"
              onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
            />
          </li>
        ))}
      </ul>
      {drug.sub_sections.map((ss) => (
        <Fragment key={ss.id}>
          <div
            className="uppercase tracking-[0.15em] text-[10px] mt-2 mb-1"
            style={{ color: accent, fontFamily: 'JetBrains Mono, monospace' }}
          >
            <EditableText
              value={ss.label}
              plain
              ariaLabel="Étiquette"
              onCommit={(t) => editHandlers?.updateDrugSubsectionLabel?.(section.id, ss.id, t)}
            />
          </div>
          <ul className="m-0 pl-4 list-disc text-[13px] leading-[1.45]">
            {ss.bullets.map((b) => (
              <li key={b.id} data-bullet-id={b.id}>
                <EditableText
                  value={b.text}
                  boldColor={ss.color ? FICHE_COLOR_HEX[ss.color] : accent}
                  ariaLabel="Bullet"
                  onCommit={(t) =>
                    editHandlers?.updateDrugSubsectionBulletText?.(section.id, ss.id, b.id, t)
                  }
                />
              </li>
            ))}
          </ul>
        </Fragment>
      ))}
    </article>
  );
}

function OtherSection({
  section,
  editHandlers,
}: {
  section: FicheSection;
  editHandlers?: FicheEditHandlers;
}) {
  if (section.type === 'mnemonic') {
    return (
      <aside
        data-section-id={section.id}
        className="bg-white rounded-control p-3 col-span-1 md:col-span-2 lg:col-span-2"
        style={{ borderLeft: '4px solid #2e6cb5' }}
      >
        <div
          className="uppercase tracking-[0.18em] text-[11px] mb-1 text-accent-blue"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Mnémo
        </div>
        <p className="font-space text-[16px] leading-[1.45]">
          <EditableText
            value={section.text}
            boldColor="#1f2330"
            ariaLabel="Mnémo"
            onCommit={(t) => editHandlers?.updateMnemonicText?.(section.id, t)}
          />
        </p>
      </aside>
    );
  }

  if (section.type === 'warning') {
    return (
      <aside
        data-section-id={section.id}
        className="bg-white rounded-control p-3"
        style={{ borderLeft: '4px solid #d34a4a' }}
      >
        <div
          className="uppercase tracking-[0.18em] text-[11px] mb-1 text-warn-text"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Avertissement
        </div>
        <p className="text-[13.5px] leading-[1.55] text-warn-text">
          <EditableText
            value={section.text}
            ariaLabel="Avertissement"
            onCommit={(t) => editHandlers?.updateWarningText?.(section.id, t)}
          />
        </p>
      </aside>
    );
  }

  if (section.type === 'clinical_case') {
    return (
      <aside
        data-section-id={section.id}
        className="bg-white rounded-control p-3 col-span-1 md:col-span-2 lg:col-span-3"
        style={{ borderLeft: '4px solid #d34a4a' }}
      >
        <div
          className="uppercase tracking-[0.18em] text-[11px] mb-1 text-warn-text"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Cas clinique
        </div>
        <p className="text-[13.5px] leading-[1.55]">
          <EditableText
            value={section.patient}
            ariaLabel="Patient"
            onCommit={(t) => editHandlers?.updateClinicalPatient?.(section.id, t)}
          />
        </p>
        <ul className="m-0 pl-4 list-disc text-[12.5px] mt-1">
          {section.prescription.map((p, i) => (
            <li key={`${section.id}-rx-${i}`}>{renderMarkdownLite(p)}</li>
          ))}
        </ul>
        <p className="text-[13.5px] mt-1">
          <strong>Question : </strong>
          <EditableText
            value={section.question}
            ariaLabel="Question"
            onCommit={(t) => editHandlers?.updateClinicalQuestion?.(section.id, t)}
          />
        </p>
        <div
          className="uppercase tracking-[0.18em] text-[10px] mt-2 mb-1 text-ink-soft"
          style={{ fontFamily: 'JetBrains Mono, monospace' }}
        >
          Conduite
        </div>
        <ul className="m-0 pl-4 list-disc text-[12.5px]">
          {section.conduite.map((b) => (
            <li key={b.id} data-bullet-id={b.id}>
              <EditableText
                value={b.text}
                ariaLabel="Bullet"
                onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
              />
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  if (section.type === 'intro' || section.type === 'classification') {
    const accent = FICHE_COLOR_HEX[section.color];
    return (
      <aside
        data-section-id={section.id}
        className="bg-white rounded-control p-3"
        style={{ borderLeft: `4px solid ${accent}` }}
      >
        <div
          className="uppercase tracking-[0.18em] text-[11px] mb-1"
          style={{ color: accent, fontFamily: 'JetBrains Mono, monospace' }}
        >
          <EditableText
            value={section.title}
            plain
            ariaLabel="Titre"
            onCommit={(t) => editHandlers?.updateSectionTitle?.(section.id, t)}
          />
        </div>
        <ul className="m-0 pl-4 list-disc text-[13px] leading-[1.5]">
          {section.bullets.map((b) => (
            <li key={b.id} data-bullet-id={b.id}>
              <EditableText
                value={b.text}
                boldColor={accent}
                ariaLabel="Bullet"
                onCommit={(t) => editHandlers?.updateBulletText?.(section.id, b.id, t)}
              />
            </li>
          ))}
        </ul>
      </aside>
    );
  }

  if (section.type === 'free_text') {
    return (
      <aside
        data-section-id={section.id}
        className="bg-white rounded-control p-3 col-span-1 md:col-span-2 lg:col-span-2"
      >
        {section.title ? (
          <div
            className="uppercase tracking-[0.18em] text-[11px] mb-1 text-ink-soft"
            style={{ fontFamily: 'JetBrains Mono, monospace' }}
          >
            {section.title}
          </div>
        ) : null}
        <p className="text-[13.5px] leading-[1.55] whitespace-pre-wrap">{section.body}</p>
      </aside>
    );
  }

  return null;
}
